import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { Edit2, Trash2, Mail, Globe, User } from 'lucide-react';

import ConfirmationDialog from '../Shared/ConfirmationDialog';
import { deleteResponsePartnerService } from '../../utils/responsePartnerService';
import { removePartner, setError } from '../../reducers/responsePartnerReducer';
import { formatEmails, formatStringList } from '../../utils/responsePartnerUtils';

const ResponsePartnerList = ({ partners = [], loading, onEdit }) => {
  const dispatch = useDispatch();
  const [pendingDeleteId, setPendingDeleteId] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleConfirmDelete = async () => {
    if (!pendingDeleteId) return;
    setIsDeleting(true);
    try {
      await deleteResponsePartnerService(pendingDeleteId);
      dispatch(removePartner(pendingDeleteId));
      setPendingDeleteId(null);
    } catch (err) {
      const message = err?.response?.data?.error || err?.message || 'Failed to delete partner';
      dispatch(setError(message));
    } finally {
      setIsDeleting(false);
    }
  };

  if (loading && partners.length === 0) {
    return <div className="text-stone-500 py-6 text-sm">Loading partners…</div>;
  }

  if (partners.length === 0) {
    return (
      <div className="border border-dashed border-stone-300 rounded-md p-6 text-center text-stone-500">
        No response partners yet. Click <span className="font-semibold">Add Partner</span> to create one.
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      {partners.map((partner) => (
        <div
          key={partner.id}
          className="border border-stone-200 rounded-md p-4 bg-white hover:border-stone-300"
        >
          <div className="flex flex-row items-start justify-between gap-3 flex-wrap">
            <div className="flex flex-col gap-1 flex-grow min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="text-lg font-semibold m-0 truncate">{partner.name}</h3>
                <span className="text-xs text-stone-500 font-mono">{partner.slug}</span>
                {partner.active ? (
                  <span className="text-xs px-2 py-0.5 rounded-3xl bg-green-100 text-green-800">
                    Active
                  </span>
                ) : (
                  <span className="text-xs px-2 py-0.5 rounded-3xl bg-stone-200 text-stone-700">
                    Inactive
                  </span>
                )}
                <span className="flex items-center gap-1 text-xs text-stone-600 px-2 py-0.5 rounded-3xl bg-stone-100">
                  {partner.ownerUserId ? <User size={12} /> : <Globe size={12} />}
                  {partner.ownerUserId ? 'User-scoped' : 'Global'}
                </span>
              </div>

              {partner.description && (
                <p className="text-sm text-stone-600 m-0">{partner.description}</p>
              )}

              {partner.serviceLines?.length > 0 && (
                <div className="text-xs text-stone-600">
                  <span className="font-semibold">Service lines:</span>{' '}
                  {formatStringList(partner.serviceLines)}
                </div>
              )}

              <div className="flex flex-col gap-1 mt-1 text-xs text-stone-600">
                <div className="flex items-center gap-1 flex-wrap">
                  <Mail size={12} />
                  <span className="font-semibold">Default emails:</span>
                  <span className="truncate">
                    {formatEmails(partner.defaultDispatchEmails) || '—'}
                  </span>
                </div>
                {(partner.afterHoursDispatchEmails?.length > 0 ||
                  partner.afterHoursStartTime ||
                  partner.afterHoursEndTime) && (
                  <div className="flex items-center gap-1 flex-wrap">
                    <span className="font-semibold">After-hours:</span>
                    <span className="truncate">
                      {formatEmails(partner.afterHoursDispatchEmails) || '—'}
                    </span>
                    {(partner.afterHoursStartTime || partner.afterHoursEndTime) && (
                      <span>
                        ({partner.afterHoursStartTime || '—'} → {partner.afterHoursEndTime || '—'}
                        {partner.dispatchTimezone ? `, ${partner.dispatchTimezone}` : ''})
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="flex flex-row items-center gap-1 shrink-0">
              <button
                onClick={() => onEdit?.(partner)}
                data-amp-track-section="Response Partner List"
                data-amp-track-action="Edit_Partner_Click"
                data-amp-track-description="Edit Response Partner"
                className="flex items-center gap-1 px-2 py-1 rounded-md bg-transparent text-stone-700 hover:bg-stone-200 text-sm"
              >
                <Edit2 size={16} /> Edit
              </button>
              <button
                onClick={() => setPendingDeleteId(partner.id)}
                data-amp-track-section="Response Partner List"
                data-amp-track-action="Delete_Partner_Click"
                data-amp-track-description="Delete Response Partner"
                className="flex items-center gap-1 px-2 py-1 rounded-md bg-transparent text-red-700 hover:bg-red-50 text-sm"
              >
                <Trash2 size={16} /> Delete
              </button>
            </div>
          </div>
        </div>
      ))}

      <ConfirmationDialog
        isOpen={Boolean(pendingDeleteId)}
        onClose={() => (isDeleting ? null : setPendingDeleteId(null))}
        onConfirm={handleConfirmDelete}
        ampSection="Response Partner List"
        message={
          <div className="text-sm text-stone-700">
            <p className="font-semibold m-0">Delete this response partner?</p>
            <p className="text-xs text-stone-500 mt-1 mb-0">
              All routing rules tied to this partner will also be removed. This cannot be undone.
            </p>
          </div>
        }
      />
    </div>
  );
};

export default ResponsePartnerList;
